/**
 * 托盘合并算法
 * 对同一客户不同唛头的托盘进行合并，减少托盘使用数
 */

import { Pallet, AlgorithmConfig, AlgorithmResult } from '../types';

/**
 * 托盘合并算法实现
 * @param pallets 托盘推荐算法的结果
 * @param config 分拣配置
 * @returns 合并后的算法执行结果
 */
export function mergePallets(pallets: Pallet[], config: AlgorithmConfig): AlgorithmResult {
  // 按 SKU 维度分组（对齐后端 PalletLoader；sku 缺省时回退 customer，兼容历史数据）
  const groupPallets = new Map<string, Pallet[]>();

  pallets.forEach(pallet => {
    const groupKey = pallet.sku ?? pallet.customer;
    if (!groupPallets.has(groupKey)) {
      groupPallets.set(groupKey, []);
    }
    groupPallets.get(groupKey)!.push(pallet);
  });

  const mergedPallets: Pallet[] = [];
  const originalPalletsCount = pallets.length;

  // 遍历每个 SKU 组的托盘
  groupPallets.forEach((groupPalletList) => {
    // 尝试合并所有托盘
    const allMerged = mergeAllPallets(groupPalletList, config);
    mergedPallets.push(...allMerged);
  });

  return {
    pallets: mergedPallets,
    totalPallets: mergedPallets.length,
    originalPallets: originalPalletsCount
  };
}

/**
 * 合并所有托盘
 * @param pallets 托盘列表
 * @param config 分拣配置
 * @returns 合并后的托盘列表
 */
function mergeAllPallets(pallets: Pallet[], config: AlgorithmConfig): Pallet[] {
  if (pallets.length <= 1) {
    return pallets;
  }

  const mergedPallets: Pallet[] = [];
  const remainingPallets = [...pallets];

  // 尝试将所有可能的托盘合并
  for (let i = 0; i < remainingPallets.length; i++) {
    const sourcePallet = remainingPallets[i];
    if (!sourcePallet) continue;

    let merged = false;

    // 尝试将当前托盘合并到已合并的托盘中
    for (let j = 0; j < mergedPallets.length; j++) {
      const targetPallet = mergedPallets[j];
      const newVolume = targetPallet.totalVolume + sourcePallet.totalVolume;
      const newWeight = targetPallet.totalWeight + sourcePallet.totalWeight;

      if (newVolume <= config.palletVolumeLimit && newWeight <= config.palletWeightLimit) {
        // 可以合并
        targetPallet.totalQuantity += sourcePallet.totalQuantity;
        targetPallet.totalVolume = newVolume;
        targetPallet.totalWeight = newWeight;
        // 合并明细，并更新源托盘明细的 palletNo 为目标托盘编号
        targetPallet.details = [
          ...targetPallet.details,
          ...sourcePallet.details.map(d => ({ ...d, palletNo: targetPallet.palletNo }))
        ];
        merged = true;
        break;
      }
    }

    if (!merged) {
      // 尝试将当前托盘与其他未合并的托盘合并
      for (let j = i + 1; j < remainingPallets.length; j++) {
        const targetPallet = remainingPallets[j];
        if (!targetPallet) continue;

        const newVolume = sourcePallet.totalVolume + targetPallet.totalVolume;
        const newWeight = sourcePallet.totalWeight + targetPallet.totalWeight;

        if (newVolume <= config.palletVolumeLimit && newWeight <= config.palletWeightLimit) {
          // 可以合并
          const mergedPalletNo = sourcePallet.palletNo; // 使用源托盘编号
          
          // 合并明细，并更新明细的 palletNo
          const mergedDetails = [
            ...sourcePallet.details.map(d => ({ ...d, palletNo: mergedPalletNo })),
            ...targetPallet.details.map(d => ({ ...d, palletNo: mergedPalletNo }))
          ];
          
          const mergedPallet: Pallet = {
            palletNo: mergedPalletNo,
            totalQuantity: sourcePallet.totalQuantity + targetPallet.totalQuantity,
            totalVolume: newVolume,
            totalWeight: newWeight,
            details: mergedDetails,
            customer: sourcePallet.customer,
            sku: sourcePallet.sku ?? targetPallet.sku
          };

          mergedPallets.push(mergedPallet);
          remainingPallets[i] = undefined!;
          remainingPallets[j] = undefined!;
          merged = true;
          break;
        }
      }
    }

    if (!merged && sourcePallet) {
      // 无法合并，直接添加
      mergedPallets.push(sourcePallet);
    }
  }

  return mergedPallets;
}



/**
 * 计算合并效率
 * @param originalCount 合并前的托盘数
 * @param mergedCount 合并后的托盘数
 * @returns 合并效率百分比
 */
export function calculateMergeEfficiency(originalCount: number, mergedCount: number): number {
  if (originalCount === 0) return 0;
  return Math.round(((originalCount - mergedCount) / originalCount) * 100);
}
